import type { DOMProps } from "@simplybusiness/mobius";
import type { Ref, RefAttributes } from "react";
import { CoverGeniusQuestionnaireActions } from "./CoverGeniusQuestionnaireActions";
import { StandardQuestionnaireActions } from "./StandardQuestionnaireActions";
import { StackedQuestionnaireActions } from "./StackedQuestionnaireActions";

// Re-export sub-components
export { BackAction, NextAction, SubmitAction } from "./components";
export type {
  BackActionProps,
  NextActionProps,
  SubmitActionProps,
} from "./components";

// Re-export compound component
export { QuestionnaireActions } from "./QuestionnaireActions";
export type { QuestionnaireActionsProps } from "./QuestionnaireActions";

// Re-export variant components
export { StandardQuestionnaireActions } from "./StandardQuestionnaireActions";
export type { StandardQuestionnaireActionsProps } from "./StandardQuestionnaireActions";

export { CoverGeniusQuestionnaireActions } from "./CoverGeniusQuestionnaireActions";
export type { CoverGeniusQuestionnaireActionsProps } from "./CoverGeniusQuestionnaireActions";

export { StackedQuestionnaireActions } from "./StackedQuestionnaireActions";
export type { StackedQuestionnaireActionsProps } from "./StackedQuestionnaireActions";

// Types for Actions router component
export type ActionsElement = HTMLDivElement;
export type ActionsRef = Ref<ActionsElement>;

/** Base props shared by all questionnaire action variants */
export type BaseQuestionnaireActionsProps = {
  /** Submit action label */
  submitActionLabel?: string;
  /** Next action label. Defaults to "Next" if not provided. */
  nextActionLabel?: string;
  /** Back action (navigates to previous section/step) */
  backAction?: { label?: string; url?: string };
};

export type ActionsVariant = "cover_genius" | "personalised_cover" | "stacked";
export type ActionsProps = BaseQuestionnaireActionsProps &
  DOMProps &
  RefAttributes<ActionsElement> & {
    /** Display variant - determines which action layout to use */
    variant?: ActionsVariant;
  };

/**
 * Smart actions component that renders the appropriate variant.
 * Each variant accesses its own state internally via hooks.
 * Analytics category is automatically determined via useNavEventCategory hook.
 *
 * Usage:
 * <Actions submitActionLabel="Continue" />
 * <Actions variant="cover_genius" submitActionLabel="Continue" />
 * <Actions variant="stacked" submitActionLabel="Submit" />
 */
export const Actions = ({ ref, ...props }: ActionsProps) => {
  const { variant, ...otherProps } = props;

  // stacked variant
  if (variant === "stacked") {
    return <StackedQuestionnaireActions {...otherProps} ref={ref} />;
  }

  // Cover Genius / Personalised Cover variant
  if (variant === "cover_genius" || variant === "personalised_cover") {
    return <CoverGeniusQuestionnaireActions {...otherProps} ref={ref} />;
  }

  // Default/standard variant
  return <StandardQuestionnaireActions {...otherProps} ref={ref} />;
};

Actions.displayName = "Actions";
